/**
 * In-memory cache that combines time-to-live (TTL) expiration with FIFO eviction.
 *
 * - TTL: Each entry has a per-set TTL. On get(), if (now - storedAt) >= ttlMs the entry is
 *   removed and undefined is returned. Expiration is lazy (checked on access), so no timer is used.
 *
 * - FIFO eviction: The cache has a max entry count. When at capacity and a new key is set (and not
 *   updating an existing key), the oldest key in insertion order is removed first. JavaScript Map
 *   iteration order is insertion order, so the first key from keys() is the oldest.
 *
 * Use for short-lived, bounded caches (e.g. request/response or editorial data) where you want
 * both automatic expiry and a hard cap on memory.
 */
interface Entry<T = unknown> {
  data: T;
  storedAt: number;
  ttlMs: number;
}

export class TtlFifoCache {
  private cache = new Map<string, Entry>();
  private readonly maxEntries: number;

  constructor(maxEntries: number) {
    this.maxEntries = maxEntries;
  }

  get<T>(key: string): T | undefined {
    const entry = this.cache.get(key) as Entry<T> | undefined;
    if (!entry) return undefined;
    if (Date.now() - entry.storedAt >= entry.ttlMs) {
      this.cache.delete(key);
      return undefined;
    }
    return entry.data;
  }

  set(key: string, data: unknown, ttlMs: number): void {
    if (this.cache.has(key)) {
      this.cache.delete(key);
    } else if (this.cache.size >= this.maxEntries) {
      const oldestKey = this.cache.keys().next().value;
      if (oldestKey !== undefined) this.cache.delete(oldestKey);
    }
    this.cache.set(key, { data, storedAt: Date.now(), ttlMs });
  }
}
